/* Dreams by Rusatsi — "Dream Current": lightweight Canvas 2D footer particle
   band. Sparse, low-opacity atmospheric dust -- not stars, not glitter, no
   glow. Left-to-right drift, paused when offscreen or the tab is hidden,
   static seeded arrangement under prefers-reduced-motion. No external
   libraries. */
(function () {
  'use strict';

  var containers = document.querySelectorAll('[data-dbr-footer-particles]');
  if (!containers.length) return;

  var reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  var COLOR_TEMPLATES = [
    'rgba(243, 237, 228, OPACITY)', /* warm ivory */
    'rgba(168, 121, 61, OPACITY)',  /* muted bronze */
    'rgba(165, 181, 202, OPACITY)'  /* rare soft blue-grey */
  ];
  var COLOR_WEIGHTS = [0.55, 0.32, 0.13];

  function mulberry32(seed) {
    return function () {
      seed |= 0;
      seed = (seed + 0x6d2b79f5) | 0;
      var t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function pickColor(rand) {
    var r = rand();
    var acc = 0;
    for (var i = 0; i < COLOR_WEIGHTS.length; i++) {
      acc += COLOR_WEIGHTS[i];
      if (r <= acc) return COLOR_TEMPLATES[i];
    }
    return COLOR_TEMPLATES[0];
  }

  function particleCountFor(viewportWidth) {
    /* Sparse dust, not a dense field. Counts nudged up modestly (was 12 /
       22 / 28-42) so the effect reads once the viewer reaches the footer,
       while staying well short of a dense field. */
    if (viewportWidth <= 480) return 15; /* mobile */
    if (viewportWidth <= 900) return 27; /* tablet */
    var t = Math.min(1, (viewportWidth - 900) / 900);
    return Math.round(34 + t * 16); /* desktop: 34-50 */
  }

  function initField(container) {
    var canvas = container.querySelector('[data-dbr-particle-canvas]');
    if (!canvas) return;
    var ctx = canvas.getContext('2d');
    var rand = mulberry32(20260720);
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var width = 0;
    var height = 0;
    var particles = [];
    var rafId = null;
    var running = false;
    var isVisible = false;
    var isTabVisible = document.visibilityState !== 'hidden';
    var lastTime = null;

    function makeParticle() {
      return {
        x: rand() * width,
        y: rand() * height,
        baseY: 0,
        speed: 3.5 + rand() * 10.5, /* 3.5-14px/s -- was 3-12, a slightly longer drift */
        /* Small and uniform -- no separate larger "soft" category, which
           was the main source of the star-like glow-prone dots. */
        radius: 0.5 + rand() * 0.9,
        /* Low opacity dust, not bright points. Raised by roughly a third
           (was 0.08-0.22) so the effect is perceptible once the viewer
           reaches the footer, without becoming bright points or a glow. */
        opacity: 0.11 + rand() * 0.18,
        color: pickColor(rand),
        phase: rand() * Math.PI * 2,
        ampY: 3 + rand() * 6,
        driftFreq: 0.15 + rand() * 0.25
      };
    }

    function resize() {
      var rect = container.getBoundingClientRect();
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      var count = particleCountFor(window.innerWidth);
      particles = [];
      for (var i = 0; i < count; i++) {
        var p = makeParticle();
        p.baseY = p.y;
        particles.push(p);
      }
    }

    function drawParticle(p, x, y) {
      /* No shadow/glow -- soft dust reads from low opacity and small radius
         alone, not from a halo, which is what made earlier versions read
         as bright stars. */
      ctx.beginPath();
      ctx.fillStyle = p.color.replace('OPACITY', p.opacity.toFixed(2));
      ctx.arc(x, y, p.radius, 0, Math.PI * 2);
      ctx.fill();
    }

    function drawFrame() {
      ctx.clearRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'source-over';
      for (var i = 0; i < particles.length; i++) {
        var p = particles[i];
        drawParticle(p, p.x, p.y);
      }
    }

    function tick(now) {
      if (!running) return;
      if (lastTime === null) lastTime = now;
      var dt = Math.min(0.05, (now - lastTime) / 1000);
      lastTime = now;

      ctx.clearRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'source-over';
      for (var i = 0; i < particles.length; i++) {
        var p = particles[i];
        p.x += p.speed * dt;
        if (p.x - p.radius > width) {
          p.x = -p.radius;
          p.y = rand() * height;
          p.baseY = p.y;
        }
        p.phase += p.driftFreq * dt;
        var y = p.baseY + Math.sin(p.phase) * p.ampY * 0.3;
        drawParticle(p, p.x, y);
      }
      rafId = requestAnimationFrame(tick);
    }

    function start() {
      if (running || reduceMotionQuery.matches) return;
      running = true;
      lastTime = null;
      rafId = requestAnimationFrame(tick);
    }

    function stop() {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = null;
    }

    function applyMotionState() {
      if (reduceMotionQuery.matches) {
        stop();
        drawFrame();
      } else if (isVisible && isTabVisible) {
        start();
      } else {
        stop();
      }
    }

    resize();
    drawFrame();

    var resizeTimer = null;
    window.addEventListener(
      'resize',
      function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          resize();
          if (!running) drawFrame();
        }, 150);
      },
      { passive: true }
    );

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            isVisible = entry.isIntersecting;
            applyMotionState();
          });
        },
        { threshold: 0.01 }
      );
      observer.observe(container);
    } else {
      isVisible = true;
      applyMotionState();
    }

    document.addEventListener('visibilitychange', function () {
      isTabVisible = document.visibilityState !== 'hidden';
      applyMotionState();
    });

    reduceMotionQuery.addEventListener('change', applyMotionState);
  }

  containers.forEach(initField);
})();
